import React from 'react'
import styled from 'styled-components'
import ArrowIcon from '../../images/arrow'
import Animate from '../animate'
import CanonicalGradient from '../../images/canonical'
import Image from 'gatsby-image'
import useIllustration from '../../hooks/use-illustration'
import useBreakpoints from '../../hooks/use-breakpoints'

const animationWrappers = {
  products: {
    delay: '1500ms',
    duration: '800ms',
    timing: 'ease-out',
  },
  crypto: {
    delay: '1500ms',
    duration: '800ms',
    timing: 'ease-out',
  },
  network: {
    delay: '300ms',
    duration: '3000ms',
    timing: 'ease-in',
  },
}
const animationProps = {
  products: i => ({
    animate: true,
    noSSR: true,
    duration: `${5000 + i * 1000}ms`,
    delay: '0ms',
    count: 'infinite',
  }),
  crypto: i => ({
    animate: true,
    noSSR: true,
    duration: `${5000 - i * 500}ms`,
    delay: '0ms',
    count: 'infinite',
  }),
  network: {
    animate: true,
    noSSR: true,
    delay: '300ms',
    duration: '8000ms',
    count: 'infinite',
  },
  leaf: {
    noSSR: true,
    delay: '1300ms',
    duration: '800ms',
  },
  circle: {
    noSSR: true,
    delay: '10ms',
    duration: '600ms',
    timing: 'ease-out',
    fill: 'backwards',
  },
  polygon: {
    noSSR: true,
    delay: '10ms',
    duration: '600ms',
    timing: 'ease-out',
    fill: 'backwards',
  },
  mainTitle: {
    noSSR: true,
    delay: '600ms',
    duration: '2800ms',
  },
  bottomTitle: {
    noSSR: true,
    delay: '2800ms',
    duration: '1000ms',
  },
  scrollDown: {
    noSSR: true,
    delay: '2800ms',
    duration: '1000ms',
    timing: 'ease-out',
  },
}

const Background = styled.div`
  height: 1000px;
  width: 100%;
  position: relative;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: ${p => p.theme.palette.primary};
  ${props => props.theme.media.md`
    height: 750px;
  `};
  ${props => props.theme.media.xs`
    height: 600px;
  `};
`

const ShapesWrapper = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  overflow: hidden;
`

const TopGradient = styled.div`
  height: 50%;
  width: 100%;
  background: -webkit-linear-gradient(
    -130deg,
    #568ee1 0%,
    #4b64d1 50%,
    #6249e3 100%
  );
  display: flex;
  justify-content: center;
  ${props => props.theme.media.xs`
    height: 100%;
  `};
`

const BottomGradient = styled(TopGradient)`
  background: -webkit-linear-gradient(
    -130deg,
    #5277d7 0%,
    #5b50dd 50%,
    #5c55e4 100%
  );
  ${props => props.theme.media.xs`
    height: 0;
  `};
`

const CircleTop = styled(CanonicalGradient)`
  top: -21vw;
  left: -19vw;
  width: 68vw;
  height: 68vw;
  border-radius: 50%;
  position: absolute;
  opacity: 0.02;
  ${p => p.css};
  animation-name: ${p => p.theme.animations.circle};
`

const CircleBottom = styled.div`
  top: -21vw;
  left: -19vw;
  width: 68vw;
  height: 68vw;
  border-radius: 50%;
  position: absolute;
  background-image: url(data:image/png;base64,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);
  opacity: 0.02;
  ${p => p.css};
  animation-name: ${p => p.theme.animations.circle};
`

const Polygon = styled.div`
  ${p => p.css};
  transform: rotate(28deg);
  animation-name: ${p => p.theme.animations.polygon};
  transform-origin: left center;
  width: 200%;
  height: 200%;
  top: -50%;
  left: 58%;
  position: absolute;
  background-color: #568ee1;
  opacity: 0.1;
  background-image: url(data:image/png;base64,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);
`

const TextWrapper = styled.div`
  text-align: center;
  padding-top: 200px;
  ${props => props.theme.media.xs`
    padding-top: 200px;
  `};
  font-weight: bold;
  position: absolute;
`

const MainTitle = styled.h1`
  font-size: 3.6rem;
  ${p => p.css};
  animation-name: ${p => p.theme.animations.landingPageTitle};
  ${props => props.theme.media.sm`
     font-size: 2.8rem;
  `};
  margin: 1.2rem 0;
`

const TopTitle = styled.h5`
  ${p => p.css};
  animation-name: ${p => p.theme.animations.landingPageTitle};
  font-size: 1.15rem;
  margin-bottom: 0;
  ${props => props.theme.media.sm`
     font-size: 0.9rem;
  `};
`

const BottomTitle = styled.h5`
  font-size: 0.95rem;
  ${p => p.css};
  animation-name: ${p => p.theme.animations.landingPageTitle};
  ${props => props.theme.media.sm`
     font-size: 0.8rem;
     width: 60%; 
     margin: auto;
     line-height: 1.5;
  `};
  color: ${p => p.theme.text.white[100]};
`

const ScrollDown = styled.div`
  ${p => p.css};
  animation-name: ${p => p.theme.animations.scrollText};
  transform: rotate(270deg) translateX(100px);
  height: 0;
  position: sticky;
  bottom: 0;
  align-self: flex-start;
  color: white;
  display: flex;
  align-items: center;
  font-weight: 900;
  font-size: 0.8rem;
  cursor: default;
  z-index: 1000;
  ${p => p.theme.media.sm`
     display: none;
  `};
`

const ScrollText = styled.div`
  color: rgba(255, 255, 255, 0.5);
`

const Arrow = styled(ArrowIcon)`
  transform: rotate(180deg);
  margin-right: 20px;
  color: rgba(255, 255, 255, 0.8);
`

const IllustrationWrapper = styled.div`
  position: absolute;
  width: 100vw;
  ${p => p.theme.media.xs`
    width: 99vw;
  `};
  height: 64%;
  bottom: 0;
  z-index: 1;
`

const ItemWrapper = styled.div`
  animation-name: ${p => p.theme.animations[p.animation]} ${p => p.css};
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
`

const StyledImage = styled(Image)`
  width: 100%;
  height: 100%;
`

const NetworkImageWrapper = styled.div`
  bottom: 5%;
  position: absolute;
  width: ${p => p.width};
  height: 100%;
  animation-name: ${p => p.theme.animations.network()};
  img {
    object-fit: contain !important;
  }
  ${p => p.theme.media.xs`
     animation-name: ${p => p.theme.animations.network(3)};
     bottom: 65%;
  `};
  ${p => `${p.side}: 0`};
  ${p => p.css};
  transform-origin: ${p => p.side};
`

const LeafContentWrapper = styled.div`
  position: absolute;
  left: 50%;
  width: 55%;
  max-width: 850px;
  transform: translateX(-59%) translateY(36%);
  ${p => p.theme.media.sm`
    width: 70%;
  `};
  ${p => p.theme.media.xs`
    width: 80%;
  `};
  margin: auto;
  bottom: 0;
`

const LeafWrapper = styled.div`
  ${p => p.css};
  animation-name: ${p => p.theme.animations.leaf()};
  ${p => p.theme.media.xs`
    animation-name: ${p => p.theme.animations.leaf(1.5)};
  `};
`

const ProductWrappers = [
  styled.div`
    ${p => p.css};
    animation-name: ${p => p.theme.animations.products()};
    filter: drop-shadow(0px 0px 40px rgba(239, 46, 25, 0.5));
    position: absolute;
    left: 11%;
    top: 3%;
    width: 17%;
    :after {
      content: '';
      width: 120%;
      height: 50%;
      top: 110%;
      right: 6%;
      background: rgba(0, 0, 0, 0.1);
      position: absolute;
      clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
      transform: skewX(37deg) rotate(-10deg);
    }
    ${p => p.theme.media.xs`
      animation-name: ${p => p.theme.animations.products(2)};
    `};
  `,
  styled.div`
    ${p => p.css};
    animation-name: ${p => p.theme.animations.products()};
    filter: drop-shadow(0px 0px 40px rgba(239, 46, 25, 0.5));
    position: absolute;
    left: 5%;
    bottom: 23%;
    width: 17%;
    ${p => p.theme.media.sm`
      left: 20%;
    `};
    ${p => p.theme.media.xs`
      animation-name: ${p => p.theme.animations.products(2)};
    `};
  `,
  styled.div`
    ${p => p.css};
    animation-name: ${p => p.theme.animations.products()};
    filter: drop-shadow(0px 0px 40px rgba(239, 46, 25, 0.5));
    position: absolute;
    right: -18%;
    top: 6%;
    width: 28%;
    :after {
      content: '';
      width: 130%;
      height: 100%;
      background: rgba(0, 0, 0, 0.1);
      position: absolute;
      left: -100%;
      bottom: -130%;
      clip-path: polygon(50% 11%, 74% 7%, 76% 32%, 74% 60%, 50% 80%, 0% 50%);
    }
    ${p => p.theme.media.xs`
      animation-name: ${p => p.theme.animations.products(2)};
    `};
  `,
]

const CryptoWrappers = [
  styled.div`
    ${p => p.css};
    animation-name: ${p => p.theme.animations.crypto()};
    filter: drop-shadow(20px 28px 38px rgb(120, 213, 189));
    position: absolute;
    left: -23%;
    top: 22%;
    width: 8%;
    ${p => p.theme.media.sm`
      left: 4%;
      width: 7%;
      top: 37%;
      animation-name: ${p => p.theme.animations.crypto(2)};
    `};
  `,
  styled.div`
    ${p => p.css};
    animation-name: ${p => p.theme.animations.crypto()};
    filter: drop-shadow(20px 28px 38px rgb(120, 213, 189));
    position: absolute;
    left: 42%;
    top: -9%;
    width: 15%;
    ${p => p.theme.media.sm`
      top: 2%;
      width: 14%;
      animation-name: ${p => p.theme.animations.crypto(2)};
    `};
  `,
  styled.div`
    ${p => p.css};
    animation-name: ${p => p.theme.animations.crypto()};
    filter: drop-shadow(20px 28px 38px rgb(120, 213, 189));
    position: absolute;
    right: -35%;
    top: 30%;
    width: 10%;
    ${p => p.theme.media.sm`
      right: -15%;
      width: 9%;
      top: 45%;
      animation-name: ${p => p.theme.animations.crypto(2)};
    `};
  `,
]

const LandingBackground = ({ animate }) => {
  const illustrationParts = useIllustration()
  const { xs } = useBreakpoints()
  return (
    <Background>
      <IllustrationWrapper>
        <Animate animate={animate} {...animationWrappers.network} noSSR>
          {props => (
            <ItemWrapper animation={'fadeIn'} {...props}>
              <Animate {...animationProps.network}>
                {props => (
                  <NetworkImageWrapper {...props} side="left" width="98%">
                    <StyledImage
                      fluid={illustrationParts.network[0].node.fluid}
                    />
                  </NetworkImageWrapper>
                )}
              </Animate>
            </ItemWrapper>
          )}
        </Animate>
        <Animate animate={animate} {...animationWrappers.network} noSSR>
          {props => (
            <ItemWrapper animation={'fadeIn'} {...props}>
              <Animate {...animationProps.network}>
                {props => (
                  <NetworkImageWrapper {...props} side="right" width="98%">
                    <StyledImage
                      fluid={illustrationParts.network[1].node.fluid}
                    />
                  </NetworkImageWrapper>
                )}
              </Animate>
            </ItemWrapper>
          )}
        </Animate>
        <LeafContentWrapper>
          <Animate animate={animate} {...animationProps.leaf} noSSR>
            {props => (
              <LeafWrapper {...props}>
                <StyledImage fluid={illustrationParts.leaf.node.fluid} />
              </LeafWrapper>
            )}
          </Animate>

          <Animate animate={animate} {...animationWrappers.products} noSSR>
            {props => (
              <ItemWrapper animation={'scaleIn'} {...props}>
                {ProductWrappers.map((_, i) => {
                  const Wrapper = ProductWrappers[i]
                  return (
                    <Animate {...animationProps.products(i)} key={i}>
                      {props => (
                        <Wrapper {...props}>
                          <StyledImage
                            fluid={illustrationParts.product[i].node.fluid}
                          />
                        </Wrapper>
                      )}
                    </Animate>
                  )
                })}
              </ItemWrapper>
            )}
          </Animate>
          <Animate animate={animate} {...animationWrappers.crypto} noSSR>
            {props => (
              <ItemWrapper animation={'scaleIn'} {...props}>
                {CryptoWrappers.map((_, i) => {
                  const Wrapper = CryptoWrappers[i]
                  return (
                    <Animate {...animationProps.crypto(i)} key={i}>
                      {props => (
                        <Wrapper {...props}>
                          <StyledImage
                            fluid={illustrationParts.crypto[i].node.fluid}
                          />
                        </Wrapper>
                      )}
                    </Animate>
                  )
                })}
              </ItemWrapper>
            )}
          </Animate>
        </LeafContentWrapper>
      </IllustrationWrapper>
      {!xs && (
        <ShapesWrapper>
          <Animate animate={animate} {...animationProps.circle}>
            {props => <CircleTop {...props} />}
          </Animate>
          <Animate animate={animate} {...animationProps.circle}>
            {props => <CircleBottom {...props} />}
          </Animate>
          <Animate animate={animate} {...animationProps.polygon}>
            {props => <Polygon {...props} />}
          </Animate>
        </ShapesWrapper>
      )}
      <TopGradient />
      <BottomGradient />
      <TextWrapper>
        <Animate animate={animate} {...animationProps.mainTitle}>
          {props => <TopTitle {...props}>NEW ERA OF</TopTitle>}
        </Animate>
        <Animate animate={animate} {...animationProps.mainTitle}>
          {props => (
            <MainTitle {...props}>DIGITAL PRODUCT DEVELOPMENT</MainTitle>
          )}
        </Animate>
        <Animate animate={animate} {...animationProps.bottomTitle}>
          {props => (
            <BottomTitle {...props}>
              We build outstanding digital products that will work tomorrow and
              beyond
            </BottomTitle>
          )}
        </Animate>
      </TextWrapper>
      <Animate animate={animate} {...animationProps.scrollDown}>
        {props => (
          <ScrollDown {...props}>
            <Arrow height={16} width={16} fill="white" />
            <ScrollText>SCROLL DOWN</ScrollText>
          </ScrollDown>
        )}
      </Animate>
    </Background>
  )
}

export default LandingBackground
